import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  AlertTriangle, Camera, MapPin, Upload, X, ShieldAlert,
  Lightbulb, MessageCircle, Send, Info, CheckCircle2, Loader2, Sparkles,
} from "lucide-react";

const TYPES = [
  { id: "idee", label: "Idée", icon: Lightbulb, color: "bg-sun-100 text-sun-700 border-sun-200" },
  { id: "suggestion", label: "Avis", icon: MessageCircle, color: "bg-mint-100 text-mint-600 border-mint-200" },
  { id: "info", label: "Question", icon: Info, color: "bg-plum-100 text-plum-700 border-plum-200" },
  { id: "probleme", label: "Problème", icon: AlertTriangle, color: "bg-pink-100 text-pink-600 border-pink-200" },
];

async function compressImage(file, maxSide = 1280, quality = 0.82) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const im = new Image();
    im.onload = () => res(im);
    im.onerror = rej;
    im.src = dataUrl;
  });
  const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  c.getContext("2d").drawImage(img, 0, 0, w, h);
  return c.toDataURL("image/jpeg", quality);
}

export default function SignalementSection() {
  const { user } = useAuth();
  const [type, setType] = useState("probleme");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [photo, setPhoto] = useState("");
  const [photoConsent, setPhotoConsent] = useState(false);
  const [coords, setCoords] = useState(null);
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [locating, setLocating] = useState(false);
  const fileRef = useRef(null);

  const onPhoto = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Photo trop volumineuse (max 8 Mo).");
      return;
    }
    try {
      const data = await compressImage(file, 1280, 0.82);
      setPhoto(data);
      toast.success("Photo ajoutée");
    } catch {
      toast.error("Impossible de lire la photo.");
    }
  };

  const locate = () => {
    if (!navigator.geolocation) {
      toast.error("Géolocalisation indisponible sur cet appareil.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success("Position détectée ✓");
        setLocating(false);
      },
      () => {
        toast.error("Position refusée — vous pouvez saisir l'adresse manuellement.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const reset = () => {
    setType("probleme"); setTitle(""); setContent(""); setPhoto("");
    setPhotoConsent(false); setCoords(null); setAddress(""); setSubmitted(false);
  };

  const submit = async () => {
    if (!user) {
      toast.info("Connectez-vous pour déposer un signalement.");
      return;
    }
    if (!title.trim() || !content.trim()) {
      toast.error("Titre et description sont requis.");
      return;
    }
    if (photo && !photoConsent) {
      toast.error("Confirmez les règles d'usage de la photo.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/feedback", {
        type, title: title.trim(), content: content.trim(),
        photo_url: photo || null,
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
        address: address.trim() || null,
      });
      setSubmitted(true);
      toast.success("Signalement envoyé ✓ La mairie vous répondra par email.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="phenix-card p-8 sm:p-10 text-center bg-gradient-to-br from-mint-50 to-cream-50 border-mint-200" data-testid="signalement-success">
        <div className="w-16 h-16 rounded-full bg-mint-100 grid place-items-center mx-auto mb-4">
          <CheckCircle2 size={32} className="text-mint-600" strokeWidth={2.2} />
        </div>
        <h3 className="heading-editorial text-2xl sm:text-3xl text-plum-900 mb-2">Signalement envoyé !</h3>
        <p className="text-[14px] text-plum-700/70 mb-6 max-w-sm mx-auto">
          La mairie d&apos;Istres a bien reçu votre message. Vous recevrez la réponse par email et dans vos notifications.
        </p>
        <Button onClick={reset} className="btn-pill bg-plum-700 hover:bg-plum-800 text-white h-11 px-6">
          Envoyer un autre signalement
        </Button>
      </div>
    );
  }

  return (
    <div className="phenix-card p-5 sm:p-7" data-testid="signalement-section">
      <div className="flex items-center justify-between mb-5 gap-3">
        <div>
          <div className="phenix-pill bg-pink-100 text-pink-600 mb-2 inline-flex">
            <Sparkles size={11} strokeWidth={2.5} /> Boîte à idées
          </div>
          <h2 className="heading-editorial text-2xl sm:text-3xl text-plum-900">
            Une idée pour Istres&nbsp;? <span className="editorial-italic text-pink-600">Partagez-la.</span>
          </h2>
          <p className="text-[13px] text-plum-700/70 mt-1 max-w-md">
            Suggestion, projet citoyen, témoignage : la mairie lit chaque message déposé ici.
          </p>
        </div>
      </div>

      {/* Type tabs — uniform sized, short labels */}
      <div className="grid grid-cols-4 gap-2 mb-5" role="radiogroup">
        {TYPES.map((t) => (
          <button
            key={t.id}
            data-testid={`signalement-type-${t.id}`}
            onClick={() => setType(t.id)}
            className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl py-3 px-1.5 sm:px-2 border-2 transition-all min-h-[78px] ${
              type === t.id
                ? `${t.color} ring-2 ring-offset-1 ring-current/30 -translate-y-0.5`
                : "bg-cream-50 text-plum-700/60 border-cream-200 hover:bg-cream-100"
            }`}
          >
            <t.icon size={20} strokeWidth={2.2} />
            <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-center leading-tight">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Form */}
      <div className="space-y-3">
        <div>
          <Label className="text-[11px] font-bold uppercase tracking-wider text-plum-700/70 mb-1.5 block">Titre court</Label>
          <Input
            data-testid="signalement-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex : Lampadaire éteint rue Gambetta"
            className="h-11 rounded-xl"
            maxLength={120}
          />
        </div>
        <div>
          <Label className="text-[11px] font-bold uppercase tracking-wider text-plum-700/70 mb-1.5 block">Description</Label>
          <Textarea
            data-testid="signalement-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Décrivez ce que vous avez constaté, où, depuis quand…"
            rows={4}
            className="rounded-xl resize-none"
            maxLength={1000}
          />
        </div>

        {/* Localisation */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Button
            data-testid="signalement-locate"
            type="button"
            variant="outline"
            onClick={locate}
            disabled={locating}
            className="h-11 rounded-xl border-cream-200 text-[12px] sm:col-span-1"
          >
            {locating ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} />}
            {coords ? "Position ✓" : locating ? "Détection…" : "Me localiser"}
          </Button>
          <Input
            data-testid="signalement-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="ou adresse / lieu approximatif"
            className="h-11 rounded-xl sm:col-span-2"
          />
        </div>
        {coords && (
          <div className="text-[11px] text-mint-600 font-semibold flex items-center gap-1.5 -mt-1">
            <CheckCircle2 size={11} /> Coordonnées GPS attachées au signalement (privées, mairie uniquement).
          </div>
        )}

        {/* Photo upload */}
        <div className="border-2 border-dashed border-cream-200 rounded-2xl p-3 bg-cream-50/50">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            data-testid="signalement-photo-input"
            onChange={(e) => onPhoto(e.target.files?.[0])}
            className="hidden"
          />
          {!photo ? (
            <button
              onClick={() => fileRef.current?.click()}
              data-testid="signalement-photo-btn"
              className="w-full flex items-center justify-center gap-2 py-3 text-plum-700 hover:text-plum-800"
            >
              <Camera size={18} strokeWidth={2.2} />
              <span className="text-[13px] font-bold">Ajouter une photo (optionnel)</span>
            </button>
          ) : (
            <div className="flex items-start gap-3">
              <img src={photo} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-bold text-plum-900">Photo ajoutée ✓</div>
                <p className="text-[11px] text-plum-700/60">Vérifiez les règles avant d&apos;envoyer.</p>
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="text-[11px] text-plum-700 hover:text-plum-800 font-semibold inline-flex items-center gap-1"
                  >
                    <Upload size={11} /> Remplacer
                  </button>
                  <button
                    onClick={() => { setPhoto(""); setPhotoConsent(false); }}
                    className="text-[11px] text-pink-600 hover:text-pink-700 font-semibold inline-flex items-center gap-1"
                  >
                    <X size={11} /> Retirer
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RGPD rules — only shown when photo is present */}
        {photo && (
          <div className="phenix-card p-4 bg-pink-50/60 border-pink-200" data-testid="signalement-rules">
            <div className="flex items-start gap-2.5">
              <ShieldAlert size={18} className="text-pink-600 shrink-0 mt-0.5" strokeWidth={2.2} />
              <div className="text-[12px] text-plum-900 leading-relaxed">
                <strong className="font-bold text-pink-700 block mb-1">Règles importantes pour les photos</strong>
                <ul className="space-y-1 text-plum-700/85 list-disc list-inside marker:text-pink-400">
                  <li><strong>Aucune personne identifiable</strong> ne doit apparaître sans son consentement explicite (droit à l&apos;image, RGPD).</li>
                  <li>Aucune plaque d&apos;immatriculation, document personnel ou visage d&apos;enfant visibles.</li>
                  <li>Concentrez la photo sur le <strong>problème</strong> (lampadaire, nid-de-poule, dépôt sauvage, etc.).</li>
                  <li>La photo sera visible uniquement par la mairie, jamais publiée publiquement.</li>
                </ul>
                <label className="mt-3 flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="signalement-photo-consent"
                    checked={photoConsent}
                    onChange={(e) => setPhotoConsent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-pink-600 cursor-pointer"
                  />
                  <span className="text-[12px] font-semibold text-plum-900">
                    Je confirme respecter ces règles. Aucune personne n&apos;est identifiable sur ma photo.
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Submit / login prompt */}
        {user ? (
          <Button
            data-testid="signalement-submit"
            onClick={submit}
            disabled={busy || (photo && !photoConsent)}
            className="w-full btn-pill bg-pink-500 hover:bg-pink-600 text-white h-12 text-[14px] font-bold shadow-[0_12px_28px_-12px_rgba(236,72,153,0.6)]"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
            Envoyer le signalement
          </Button>
        ) : (
          <Link to="/login" className="block">
            <Button className="w-full btn-pill bg-plum-700 hover:bg-plum-800 text-white h-12 text-[14px]" data-testid="signalement-login-btn">
              Se connecter pour envoyer <ArrowRight size={15} />
            </Button>
          </Link>
        )}

        <p className="text-[11px] text-plum-700/50 text-center">
          Votre identité reste privée — seul votre pseudo est visible publiquement.
        </p>
      </div>
    </div>
  );
}

function ArrowRight(props) {
  return (
    <svg width={props.size || 16} height={props.size || 16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}
